import { Button, ConfigProvider, Flex, Input, Radio, Tag, theme, Typography } from 'antd'
import React, { CSSProperties, useOptimistic, useTransition } from 'react'
import { useTranslation } from 'react-i18next'
import { TYPE, useCreateCampaignStepsContext } from '../campaign-create-steps-context'
import clsx from 'clsx'
import { Paragraph } from '../paragraph'
import { Title } from '../title'
import { FormItem } from '../../../../components/form-item'
import { getCampaignAgentConfig } from '../campaign-agent-config'
import { AgentType } from '../../../agent-type'
import './type-step.css'

export function TypeStep() {
    const { t } = useTranslation()
    const { token } = theme.useToken()
    const { currentStep, availableTypes, selectedType, noAvailableTypes, selectType, nextStep } =
        useCreateCampaignStepsContext()
    // Show the choice at once. Selecting a type re-renders every step and mounts the next step,
    // so that work runs as a transition after the option is shown as selected.
    const [, startTransition] = useTransition()
    const [shownType, setShownType] = useOptimistic(selectedType)

    const styles = {
        '--campaign-type-border': token.colorBorder,
        '--campaign-type-active': token.colorPrimary,
        '--campaign-type-selected-background': token.colorPrimaryBg,
    } as CSSProperties

    return (
        <div className={clsx({ hidden: currentStep !== TYPE })}>
            <Title>{t('What do you want to achieve with this campaign?')}</Title>
            {noAvailableTypes ? (
                <Paragraph>{t('No campaign types are available for your account right now.')}</Paragraph>
            ) : (
                <>
                    {/* Keeps the type in the submitted values; selectType sets it. */}
                    <FormItem
                        name="type"
                        noStyle
                    >
                        <Input hidden />
                    </FormItem>
                    {/* The click ripple on these large cards costs a render on every click and adds little. */}
                    <ConfigProvider wave={{ disabled: true }}>
                        <Radio.Group
                            className="mb-6 w-full"
                            style={styles}
                            name="campaign-type"
                            aria-label={t('Campaign type')}
                            value={shownType}
                            onChange={(event) => {
                                const type = event.target.value
                                startTransition(() => {
                                    setShownType(type)
                                    selectType(type)
                                })
                            }}
                        >
                            <Flex
                                vertical
                                gap="middle"
                            >
                                {availableTypes.map((type) => (
                                    <CampaignTypeOption
                                        key={type}
                                        type={type}
                                        selected={shownType === type}
                                    />
                                ))}
                            </Flex>
                        </Radio.Group>
                    </ConfigProvider>

                    <Paragraph>
                        {t('For every type, you can set up WhatsApp and candidate sources and test the conversation.')}
                    </Paragraph>

                    <Button
                        disabled={!selectedType}
                        size="large"
                        type="primary"
                        onClick={nextStep}
                    >
                        {t('Continue')}
                    </Button>
                </>
            )}
        </div>
    )
}

type CampaignTypeOptionProps = {
    type: AgentType
    selected: boolean
}

function CampaignTypeOption({ type, selected }: CampaignTypeOptionProps) {
    const { t } = useTranslation()
    const config = getCampaignAgentConfig(type)

    if (!config) {
        return null
    }

    return (
        <Radio
            value={type}
            className={clsx('campaign-type-option', selected && 'campaign-type-option-selected')}
        >
            <Flex
                vertical
                gap={8}
            >
                <Flex
                    wrap
                    gap={8}
                    align="center"
                >
                    <Typography.Text strong>{t(config.label)}</Typography.Text>
                    {config.internal && <Tag>{t('Internal')}</Tag>}
                </Flex>
                <Typography.Text>{t(config.description)}</Typography.Text>
                <Flex
                    vertical
                    gap={4}
                >
                    <Typography.Text type="secondary">
                        <Typography.Text strong>{t('You start with:')}</Typography.Text> {t(config.startingPoint)}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                        <Typography.Text strong>{t('You configure:')}</Typography.Text> {t(config.setup)}
                    </Typography.Text>
                </Flex>
            </Flex>
        </Radio>
    )
}
